import { useState } from 'react';
import { Flag } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const REASONS = [
  { value: 'copyright', label: 'Copyright / IP infringement' },
  { value: 'nudity', label: 'Nudity / sexual content' },
  { value: 'hate', label: 'Hate speech or symbols' },
  { value: 'violence', label: 'Violence or gore' },
  { value: 'personal_info', label: 'Personal information / doxxing' },
  { value: 'spam', label: 'Spam or misleading' },
  { value: 'other', label: 'Something else' },
];

/**
 * Small flag icon that opens a modal to submit a report on any social entity.
 * Props:
 *   entityType: 'user_design' | 'design' | 'product'
 *   entityId: string
 *   variant?: 'icon' | 'full'   default 'icon'
 *   className?: string
 */
export const ReportButton = ({ entityType, entityId, variant = 'icon', className = '' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('copyright');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);

  const openDialog = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!user) { toast('Please sign in first'); navigate('/login'); return; }
    setReason('copyright'); setDetails(''); setOpen(true);
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/reports', { entity_type: entityType, entity_id: entityId, reason, details });
      if (data.already_reported) toast('You already reported this — our team is reviewing.');
      else toast.success('Report submitted — thank you.');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally { setBusy(false); }
  };

  const stop = (e) => e.stopPropagation();

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={openDialog}
          data-testid={`report-${entityType}-${entityId}-icon`}
          className={`p-1.5 rounded-full border transition-colors flex items-center gap-1 text-[11px] border-white/10 hover:bg-white/[0.06] ${className}`}
          style={{ color: 'var(--jw-text-muted)' }}
          aria-label="Report content"
          title="Report"
        >
          <Flag className="w-3.5 h-3.5" />
        </button>
      ) : (
        <Button
          type="button"
          onClick={openDialog}
          data-testid={`report-${entityType}-${entityId}-button`}
          variant="ghost"
          size="sm"
          className={`gap-1 text-xs ${className}`}
          style={{ color: 'var(--jw-text-muted)' }}
        >
          <Flag className="w-3.5 h-3.5" /> Report
        </Button>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
        <DialogContent onClick={stop} data-testid={`report-${entityType}-${entityId}-dialog`} className="max-w-md">
          <DialogHeader>
            <DialogTitle>Report content</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>
              Reports are reviewed by our moderators. False reports can lead to restrictions on your account — please only report content that clearly breaks the rules.
            </p>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Reason</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="report-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (<SelectItem key={r.value} value={r.value} data-testid={`report-reason-${r.value}`}>{r.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>Details (optional)</label>
              <Textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} data-testid="report-details-input" placeholder="Tell us what makes this content problematic (optional)" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="report-submit-button" className="jw-btn-glossy">
              {busy ? 'Submitting…' : 'Submit report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportButton;
